/** Data hooks for Phase 8: plan and usage, online renewal, single sign-on, payment links,
 * e-signature, cloud accounting connections and the HubSpot import. */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { api, qs } from "./client";
import type { ImportResult, UserRef } from "./types";

// ---- plan and usage ----
export interface Plan {
  key: string; name: string; description: string | null; price_per_user: number; currency: string; max_users: number | null;
  limits: Record<string, number | null>; features: string[]; is_public: boolean; position: number; workspaces: number;
}
export interface Meter { key: string; label: string; used: number; limit: number | null; share: number | null }
export interface PlatformInvoice { id: string; number: string; description: string; amount: number; tax: number; total: number; currency: string; issued_at: string }
export interface Billing {
  plan: Plan | null; plan_key: string; licence_expires_at: string | null; meters: Meter[]; features: string[];
  online_renewal: boolean; plans: Plan[]; invoices: PlatformInvoice[]; support_email: string;
}
export interface Checkout { order_id: string; amount: number; tax: number; total: number; currency: string; checkout_url: string }

export const useBilling = (enabled = true) => useQuery({ queryKey: ["billing"], queryFn: () => api<Billing>("GET", "/billing"), enabled });
export const useCheckout = () =>
  useMutation({ mutationFn: (body: { plan_key: string; users: number; months: number }) => api<Checkout>("POST", "/billing/checkout", body) });

// ---- single sign-on ----
export interface SsoSettings {
  protocol: "oidc" | "saml"; enabled: boolean; enforce: boolean; label: string; allowed_domains: string[];
  auto_provision: boolean; default_role: string; issuer: string | null; client_id: string | null; has_client_secret: boolean;
  idp_entity_id: string | null; idp_sso_url: string | null; idp_certificate: string | null;
  redirect_uri: string; sp_entity_id: string; acs_url: string; metadata_url: string;
}
export type SsoInput = Partial<Omit<SsoSettings, "has_client_secret" | "redirect_uri" | "sp_entity_id" | "acs_url" | "metadata_url">> & { client_secret?: string | null };
export const useSso = () => useQuery({ queryKey: ["sso"], queryFn: () => api<SsoSettings>("GET", "/sso") });
export function useSaveSso() {
  const qc = useQueryClient();
  return useMutation({ mutationFn: (body: SsoInput) => api<SsoSettings>("PUT", "/sso", body), onSuccess: (s) => qc.setQueryData(["sso"], s) });
}

// ---- payment links ----
export interface PaymentConnection {
  connected: boolean; kind: "razorpay" | "stripe" | null; key_id: string | null; is_active: boolean; has_secret: boolean;
  has_webhook_secret: boolean; webhook_url: string | null; last_event_at: string | null;
}
export interface PaymentRequest {
  id: string; kind: string; description: string; amount: number; currency: string; status: "sent" | "paid" | "cancelled";
  url: string | null; payment_ref: string | null; paid_at: string | null; payer_email: string | null; sent_to: string | null;
  requested_by: UserRef | null; created_at: string;
}
export const usePaymentConnection = () => useQuery({ queryKey: ["payments-conn"], queryFn: () => api<PaymentConnection>("GET", "/integrations/payments") });
export function useSavePayments() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { kind: string; key_id?: string; secret?: string; webhook_secret?: string; is_active?: boolean }) =>
      api<PaymentConnection>("PUT", "/integrations/payments", body),
    onSuccess: (c) => qc.setQueryData(["payments-conn"], c),
  });
}
export const usePaymentRequests = (filter: { order_id?: string; quote_id?: string }) =>
  useQuery({
    queryKey: ["payment-requests", filter], queryFn: () => api<PaymentRequest[]>("GET", `/payment-requests${qs(filter)}`),
    // A link that is out waits for the customer; watch for the payment.
    refetchInterval: (q) => (q.state.data?.some((r) => r.status === "sent") ? 5000 : false),
  });
export function usePaymentAction() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ path, body }: { path: string; body?: unknown }) => api<PaymentRequest>("POST", path, body ?? {}),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: ["payment-requests"] }); void qc.invalidateQueries({ queryKey: ["timeline"] }); },
  });
}

// ---- e-signature ----
export interface EsignConnection { connected: boolean; region: string | null; is_active: boolean; last_error: string | null }
export interface EsignRequest {
  id: string; document_type: "quote" | "order"; title: string; recipient_name: string; recipient_email: string;
  status: "sent" | "completed" | "declined" | "expired" | "failed" | string; signed_at: string | null; error: string | null;
  requested_by: UserRef | null; created_at: string;
}
export const useEsignConnection = () => useQuery({ queryKey: ["esign-conn"], queryFn: () => api<EsignConnection>("GET", "/integrations/esign") });
export function useSaveEsign() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { region: string; client_id?: string; client_secret?: string; grant_code?: string; is_active?: boolean }) =>
      api<EsignConnection>("PUT", "/integrations/esign", body),
    onSuccess: (c) => qc.setQueryData(["esign-conn"], c),
  });
}
export const useEsignRequests = (filter: { order_id?: string; quote_id?: string }) =>
  useQuery({
    queryKey: ["esign-requests", filter], queryFn: () => api<EsignRequest[]>("GET", `/esign-requests${qs(filter)}`),
    refetchInterval: (q) => (q.state.data?.some((r) => r.status === "sent") ? 10000 : false),
  });
export function useEsignAction() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ path, body }: { path: string; body?: unknown }) => api<EsignRequest>("POST", path, body ?? {}),
    onSuccess: () => {
      for (const key of ["esign-requests", "quote", "quotes", "timeline"]) void qc.invalidateQueries({ queryKey: [key] });
    },
  });
}

// ---- HubSpot import ----
export function useHubSpotImport() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { token: string; dry_run: boolean; objects: string[]; stage_map: Record<string, string>; pipeline_id?: string | null }) =>
      api<ImportResult[]>("POST", "/imports/hubspot", body),
    onSuccess: (r) => { if (r.some((x) => !x.dry_run)) void qc.invalidateQueries(); },
  });
}
